import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft,
  Warehouse,
  PackagePlus,
  PackageMinus,
  Loader2,
  CheckCircle2,
  Camera
} from 'lucide-react';
import ScanQrModal from './ScanQrModal';

const call = (name, data) => httpsCallable(functions, name)(data);

const ArmazemOperacaoPage = () => {
  const { getEmpresaId } = useAuth();
  const navigate = useNavigate();
  const empresaId = getEmpresaId();

  const [aba, setAba] = useState('guardar');
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(null); // { campo, titulo }
  const [guardar, setGuardar] = useState({ pallet: '', posicao: '' });
  const [palletsAguardando, setPalletsAguardando] = useState([]);
  const [sugestao, setSugestao] = useState(null);

  const [produtos, setProdutos] = useState([]);
  const [saidaProduto, setSaidaProduto] = useState('');
  const [saidaQtd, setSaidaQtd] = useState(1);
  const [saidaAtual, setSaidaAtual] = useState(null);
  const [retirada, setRetirada] = useState({ pallet: '', posicao: '' });

  const loadAguardando = useCallback(async () => {
    try {
      const r = await call('listArmazemPallets', { empresa_id: empresaId, status: 'aguardando' });
      setPalletsAguardando(r.data?.data?.items || []);
    } catch {
      setPalletsAguardando([]);
    }
  }, [empresaId]);

  const loadArmazenados = useCallback(async () => {
    try {
      const r = await call('listArmazemPallets', { empresa_id: empresaId, status: 'armazenado' });
      const items = r.data?.data?.items || [];
      const map = new Map();
      items.forEach((p) => {
        const cur = map.get(p.produto_id) || { id: p.produto_id, nome: p.produto_nome, qtd: 0 };
        cur.qtd += 1;
        map.set(p.produto_id, cur);
      });
      setProdutos([...map.values()]);
    } catch {
      setProdutos([]);
    }
  }, [empresaId]);

  useEffect(() => {
    loadAguardando();
    loadArmazenados();
  }, [loadAguardando, loadArmazenados]);

  const sugerir = async (palletId) => {
    setBusy(true);
    setErr('');
    try {
      const r = await call('sugerirPosicoesPallets', { empresa_id: empresaId, pallet_ids: [palletId] });
      const s = r.data?.data?.sugestoes?.[0];
      setSugestao(s || null);
      if (s?.pallet?.qr_payload) setGuardar((g) => ({ ...g, pallet: s.pallet.qr_payload }));
      setMsg(
        s?.posicao
          ? `Leve o pallet para ${s.posicao.endereco} (câmara ${s.posicao.camara_codigo}, rua ${s.posicao.rua_codigo}, andar ${s.posicao.andar_codigo}).`
          : 'Sem sugestão.'
      );
    } catch (e) {
      setErr(e.message || 'Não foi possível sugerir posição');
    } finally {
      setBusy(false);
    }
  };

  const confirmarGuarda = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await call('confirmarArmazenagemPallet', {
        empresa_id: empresaId,
        pallet_scan: guardar.pallet,
        posicao_scan: guardar.posicao
      });
      setMsg(r.data?.data?.message || 'Armazenado.');
      setGuardar({ pallet: '', posicao: '' });
      setSugestao(null);
      await loadAguardando();
      await loadArmazenados();
    } catch (e) {
      setErr(e.message || 'Falha ao confirmar armazenagem');
    } finally {
      setBusy(false);
    }
  };

  const planejar = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await call('planejarSaidaPallets', {
        empresa_id: empresaId,
        produto_id: saidaProduto,
        quantidade_pallets: Number(saidaQtd)
      });
      setSaidaAtual(r.data?.data);
      setMsg('Ordem de saída gerada (FEFO). Siga as posições e leia os QRs na retirada.');
    } catch (e) {
      setErr(e.message || 'Não foi possível planejar a saída');
    } finally {
      setBusy(false);
    }
  };

  const confirmarRetirada = async () => {
    setBusy(true);
    setErr('');
    try {
      const r = await call('confirmarRetiradaPallet', {
        empresa_id: empresaId,
        pallet_scan: retirada.pallet,
        posicao_scan: retirada.posicao,
        saida_id: saidaAtual?.id
      });
      setMsg(r.data?.data?.message || 'Retirado.');
      setRetirada({ pallet: '', posicao: '' });
      if (saidaAtual) {
        const items = (saidaAtual.items || []).map((it) => {
          const match =
            retirada.pallet.includes(it.pallet_id) ||
            retirada.pallet.includes(it.codigo) ||
            it.codigo === retirada.pallet;
          return match ? { ...it, retirado: true } : it;
        });
        setSaidaAtual({ ...saidaAtual, items });
      }
      await loadArmazenados();
    } catch (e) {
      setErr(e.message || 'Falha ao confirmar retirada');
    } finally {
      setBusy(false);
    }
  };

  const onScan = useCallback(
    (valor) => {
      if (!scan) return;
      if (scan.campo === 'g_pallet') setGuardar((g) => ({ ...g, pallet: valor }));
      if (scan.campo === 'g_pos') setGuardar((g) => ({ ...g, posicao: valor }));
      if (scan.campo === 'r_pallet') setRetirada((g) => ({ ...g, pallet: valor }));
      if (scan.campo === 'r_pos') setRetirada((g) => ({ ...g, posicao: valor }));
      setScan(null);
    },
    [scan]
  );

  const pendentesSaida = useMemo(
    () => (saidaAtual?.items || []).filter((i) => !i.retirado),
    [saidaAtual]
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="sticky top-0 z-20 flex items-center gap-3 bg-gray-900 px-4 py-3 text-white">
        <button type="button" onClick={() => navigate('/app/armazenamento')} className="p-2 rounded-lg hover:bg-white/10">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <Warehouse className="h-6 w-6 text-emerald-400" />
        <div>
          <h1 className="text-lg font-bold leading-tight">Operação de armazém</h1>
          <p className="text-xs text-gray-300">Guarda e retirada de pallets</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-4">
        {err && <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">{err}</div>}
        {msg && <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900">{msg}</div>}

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setAba('guardar')}
            className={`rounded-xl py-3 font-semibold ${
              aba === 'guardar' ? 'bg-emerald-600 text-white' : 'bg-white border'
            }`}
          >
            Guardar
          </button>
          <button
            type="button"
            onClick={() => setAba('retirar')}
            className={`rounded-xl py-3 font-semibold ${
              aba === 'retirar' ? 'bg-emerald-600 text-white' : 'bg-white border'
            }`}
          >
            Retirar
          </button>
        </div>

        {aba === 'guardar' && (
          <section className="rounded-xl bg-white border p-4 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <PackagePlus className="h-5 w-5 text-emerald-700" /> Entrada no porta-pallet
            </h2>
            {palletsAguardando.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs text-gray-500">Aguardando guarda:</p>
                {palletsAguardando.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className="w-full text-left text-sm border rounded-lg px-3 py-2 hover:bg-emerald-50"
                    onClick={() => sugerir(p.id)}
                  >
                    <span className="font-mono font-semibold">{p.codigo}</span> · {p.produto_nome} · val.{' '}
                    {p.data_validade || '—'}
                  </button>
                ))}
              </div>
            )}
            {sugestao?.posicao && (
              <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm">
                Posição sugerida: <strong>{sugestao.posicao.endereco}</strong>
                <br />
                Câmara {sugestao.posicao.camara_nome} · Rua {sugestao.posicao.rua_codigo} · Andar{' '}
                {sugestao.posicao.andar_codigo}
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-2">
              <button
                type="button"
                className="border rounded-lg px-3 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 bg-emerald-50 border-emerald-300"
                onClick={() => setScan({ campo: 'g_pallet', titulo: 'Ler QR do pallet' })}
              >
                <Camera className="h-5 w-5" /> Ler pallet {guardar.pallet ? '✓' : ''}
              </button>
              <button
                type="button"
                className="border rounded-lg px-3 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 bg-emerald-50 border-emerald-300"
                onClick={() => setScan({ campo: 'g_pos', titulo: 'Ler QR da posição' })}
              >
                <Camera className="h-5 w-5" /> Ler posição {guardar.posicao ? '✓' : ''}
              </button>
            </div>
            <button
              type="button"
              disabled={busy || !guardar.pallet || !guardar.posicao}
              onClick={confirmarGuarda}
              className="w-full bg-emerald-600 text-white rounded-lg py-3 font-semibold disabled:opacity-50 inline-flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
              Confirmar armazenagem
            </button>
          </section>
        )}

        {aba === 'retirar' && (
          <section className="rounded-xl bg-white border p-4 space-y-3">
            <h2 className="font-semibold flex items-center gap-2">
              <PackageMinus className="h-5 w-5 text-orange-700" /> Saída FEFO
            </h2>
            <label className="text-sm block">
              Produto
              <select
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={saidaProduto}
                onChange={(e) => setSaidaProduto(e.target.value)}
              >
                <option value="">Selecione…</option>
                {produtos.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome} ({p.qtd} pallet)
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm block">
              Quantidade de pallets
              <input
                type="number"
                min={1}
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={saidaQtd}
                onChange={(e) => setSaidaQtd(e.target.value)}
              />
            </label>
            <button
              type="button"
              disabled={busy || !saidaProduto}
              onClick={planejar}
              className="w-full border border-orange-600 text-orange-800 rounded-lg py-2 font-semibold disabled:opacity-50"
            >
              Indicar pallets (validade mais próxima)
            </button>

            {saidaAtual && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Retire nesta ordem:</p>
                {(saidaAtual.items || []).map((it) => (
                  <div
                    key={it.pallet_id}
                    className={`text-sm rounded-lg border px-3 py-2 ${
                      it.retirado ? 'bg-gray-50 text-gray-400' : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <span className="font-mono font-semibold">{it.codigo}</span> · {it.endereco}
                    <br />
                    Val. {it.data_validade || '—'} · {it.quantidade} {it.unidade}
                    {it.retirado ? ' · retirado' : ''}
                  </div>
                ))}
                {pendentesSaida.length > 0 && (
                  <>
                    <div className="grid sm:grid-cols-2 gap-2">
                      <button
                        type="button"
                        className="border rounded-lg px-3 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 bg-orange-50 border-orange-300"
                        onClick={() => setScan({ campo: 'r_pallet', titulo: 'Ler QR do pallet' })}
                      >
                        <Camera className="h-5 w-5" /> Ler pallet {retirada.pallet ? '✓' : ''}
                      </button>
                      <button
                        type="button"
                        className="border rounded-lg px-3 py-4 text-sm font-semibold inline-flex items-center justify-center gap-2 bg-orange-50 border-orange-300"
                        onClick={() => setScan({ campo: 'r_pos', titulo: 'Ler QR da posição' })}
                      >
                        <Camera className="h-5 w-5" /> Ler posição {retirada.posicao ? '✓' : ''}
                      </button>
                    </div>
                    <button
                      type="button"
                      disabled={busy || !retirada.pallet || !retirada.posicao}
                      onClick={confirmarRetirada}
                      className="w-full bg-orange-600 text-white rounded-lg py-3 font-semibold disabled:opacity-50"
                    >
                      Confirmar retirada
                    </button>
                  </>
                )}
              </div>
            )}
          </section>
        )}
      </div>

      <ScanQrModal
        open={!!scan}
        title={scan?.titulo}
        onCancel={() => setScan(null)}
        onRead={onScan}
      />
    </div>
  );
};

export default ArmazemOperacaoPage;
